import {
  Component,
  effect,
  inject,
  Input,
  PLATFORM_ID,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnimationItem } from 'lottie-web';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { AuthService } from '../../../core/services/auth.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import {
  errorToastAction,
  loadingToastAction,
  successToastAction,
} from '../../../core/store/actions/toast.actions';
import { SvgIconComponent } from 'angular-svg-icon';
import { EmailVerificationFailedComponent } from '../email-verification-failed/email-verification-failed.component';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
  selector: 'app-email-verified',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    EmailVerificationFailedComponent,
    NgxSpinnerModule,
    NzButtonModule,
  ],
  templateUrl: './email-verified.component.html',
  styleUrl: './email-verified.component.css',
})
export class EmailVerifiedComponent {
  platformId = inject(PLATFORM_ID);
  isBrowser = isPlatformBrowser(this.platformId);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  router = inject(Router);
  store = inject(Store<AppState>);
  spinnerService = inject(NgxSpinnerService);

  @Input() email?: string;
  token = injectQueryParams('token');
  verificationStatus = injectQueryParams('status');
  emailSignal = injectQueryParams('email');

  verifyEmailResponse = injectQuery(() => {
    return {
      queryKey: ['verify-email-confirmation', this.token()],
      queryFn: ({ queryKey }) => {
        const [, token] = queryKey;
        return this.authService.verifyEmail(<string>token);
      },
      // Avoid duplicate verification call during SSR + hydration.
      enabled: this.isBrowser && !!this.token() && !this.verificationStatus(),
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 0,
    };
  });

  onAnimate(animationItem: AnimationItem): void {}

  get isLoading() {
    return (
      this.verifyEmailResponse.isPending() &&
      this.verifyEmailResponse.fetchStatus() !== 'idle'
    );
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.spinnerService.show();
  }

  constructor() {
    effect(() => {
      const isLoading =
        this.verifyEmailResponse.isPending() &&
        this.verifyEmailResponse.fetchStatus() !== 'idle';
      untracked(() => {
        if (isLoading) {
          this.store.dispatch(
            loadingToastAction({
              autohide: false,
              delay: 7000,
              placement: 'top-end',
              message: 'Verifying email. Please wait...',
            }),
          );
        }
      });
    });
    effect(() => {
      const isErrror = this.verifyEmailResponse?.isError();
      const isSuccess = this.verifyEmailResponse?.isSuccess();
      untracked(() => {
        if (isErrror) {
          this.spinnerService.hide();
          const error = this.verifyEmailResponse.error();
          const errorMessage = (error as any)?.error?.message;

          this.store.dispatch(
            errorToastAction({
              autohide: true,
              delay: 7000,
              placement: 'top-end',
              message:
                errorMessage ||
                'An error occurred while verifying email. Try again shortly',
            }),
          );
          this.router.navigate([], {
            queryParams: {
              status: 'failure',
            },
            queryParamsHandling: 'merge',
          });
        }
        if (isSuccess) {
          const data = this.verifyEmailResponse.data();
          this.authService.setPreRegData(data);
          this.authService.setUserData(data);
          this.spinnerService.hide();
          this.store.dispatch(
            successToastAction({
              autohide: true,
              delay: 7000,
              placement: 'top-end',
              message: 'Email verified successfully',
            }),
          );
          this.router.navigate(['auth', 'reset_password'], {
            queryParams: {
              token: data.passwordResetToken,
            },
            queryParamsHandling: 'merge',
          });
        }
      });
    });
  }

  get isError() {
    return this.verifyEmailResponse.isError();
  }

  switchToLogin() {
    this.router.navigate(['/']);
  }
}
